import React, { useState, useEffect } from "react";
import { Box, Stack, Typography, Paper, IconButton, Collapse, Button, CircularProgress, Chip } from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { QuizStyledCard } from "../../shared/QuizStyledCard";
import { COLORS } from "../constants";
import { formatDate, formatPrice } from "../helpers";
import { useRechargeApi } from "../hooks/useRechargeApi";
import { extractLineItemsWithImages, groupLineItems } from "../../../utils/lineItems";
import type { SubscriptionData } from "../types";
import { QUIZ } from "@/theme/quizTokens";

interface Props {
  customerId: string;
  products: any[];
  subscriptions: SubscriptionData[];
}

export function OrderHistorySection({ customerId, products, subscriptions }: Props) {
  const [show, setShow] = useState(true);
  const [charges, setCharges] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [visibleCount, setVisibleCount] = useState(10);
  const { call } = useRechargeApi();

  useEffect(() => {
    if (!customerId) return;
    let cancelled = false;

    const fetchCharges = async () => {
      setLoading(true);
      try {
        const res = await call<any>("/recharge/charges", {
          params: { customer_id: customerId },
        });
        if (!cancelled && res.ok) {
          const allCharges = res.data?.charges || [];
          // Sort by date descending
          allCharges.sort((a: any, b: any) => {
            const da = new Date(a.processed_at || a.scheduled_at || 0).getTime();
            const db = new Date(b.processed_at || b.scheduled_at || 0).getTime();
            return db - da;
          });
          setCharges(allCharges);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCharges();
    return () => { cancelled = true; };
  }, [customerId, call]);

  const statusColor = (status: string) => {
    const s = status?.toLowerCase();
    if (s === "success") return { bg: COLORS.greenLight, color: COLORS.green };
    if (s === "refunded" || s === "partially_refunded") return { bg: COLORS.blueLight, color: COLORS.blue };
    if (s === "error" || s === "failed") return { bg: COLORS.redLight, color: COLORS.red };
    if (s === "queued" || s === "pending") return { bg: COLORS.orangeBg, color: COLORS.orangeText };
    return { bg: QUIZ.color.cream2, color: COLORS.textMuted };
  };

  const visibleCharges = charges.slice(0, visibleCount);

  return (
    <QuizStyledCard>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }} onClick={() => setShow(!show)}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ReceiptLongIcon sx={{ color: COLORS.brown }} />
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: COLORS.brownDeep }}>
            Order History ({charges.length})
          </Typography>
        </Box>
        <IconButton size="small">
          {show ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Box>

      <Collapse in={show} timeout="auto" unmountOnExit>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : charges.length === 0 ? (
          <Typography sx={{ mt: 2, fontSize: 13, color: COLORS.textMuted }}>No order history found</Typography>
        ) : (
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {visibleCharges.map((charge: any) => {
              const lineItems = extractLineItemsWithImages(charge, products, subscriptions);
              const grouped = groupLineItems(lineItems);
              const { bg, color } = statusColor(charge.status);
              const total = parseFloat(charge.total_price || charge.subtotal_price || "0");
              const orderDate = charge.processed_at || charge.scheduled_at;

              return (
                <Paper
                  key={charge.id}
                  sx={{ p: 2, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}` }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 14, color: COLORS.brownDeep }}>
                          #{charge.id}
                        </Typography>
                        <Chip
                          label={charge.status}
                          size="small"
                          sx={{ bgcolor: bg, color, fontWeight: 600, fontSize: 11, height: 22 }}
                        />
                      </Box>
                      <Typography sx={{ fontSize: 12, color: COLORS.textMuted }}>
                        {formatDate(orderDate)}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 16, color: COLORS.brownDeep }}>
                      {formatPrice(total)}
                    </Typography>
                  </Box>

                  {/* Line items grouped by type */}
                  {grouped.bundles.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {grouped.bundles.map((item, idx) => (
                        <Box key={idx} sx={{ mb: 0.5 }}>
                          <Typography sx={{ fontSize: 12, color: COLORS.brown, fontWeight: 600 }}>
                            {item.quantity}x {item.productTitle}
                          </Typography>
                          {item.subItems && item.subItems.length > 0 && (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, ml: 1.5, mt: 0.5 }}>
                              {item.subItems.map((sub, sIdx) => (
                                <Chip
                                  key={sIdx}
                                  label={`${sub.quantity}x ${sub.productTitle}`}
                                  size="small"
                                  sx={{ bgcolor: COLORS.orangeLight, fontSize: 10, height: 20 }}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {grouped.meals.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                      {grouped.meals.map((item, idx) => (
                        <Chip
                          key={idx}
                          label={`${item.quantity}x ${item.productTitle}`}
                          size="small"
                          sx={{ bgcolor: COLORS.orangeLight, fontSize: 10, height: 20 }}
                        />
                      ))}
                    </Box>
                  )}

                  {grouped.treats.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                      {grouped.treats.map((item, idx) => (
                        <Chip
                          key={idx}
                          label={`${item.quantity}x ${item.productTitle}`}
                          size="small"
                          sx={{ bgcolor: COLORS.greenLight, fontSize: 10, height: 20 }}
                        />
                      ))}
                    </Box>
                  )}

                  {grouped.other.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                      {grouped.other.map((item, idx) => (
                        <Chip
                          key={idx}
                          label={`${item.quantity}x ${item.productTitle}`}
                          size="small"
                          sx={{ bgcolor: QUIZ.color.cream2, fontSize: 10, height: 20 }}
                        />
                      ))}
                    </Box>
                  )}
                </Paper>
              );
            })}

            {charges.length > visibleCount && (
              <Button
                variant="outlined"
                onClick={() => setVisibleCount(prev => prev + 10)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Show More ({charges.length - visibleCount} remaining)
              </Button>
            )}
          </Stack>
        )}
      </Collapse>
    </QuizStyledCard>
  );
}
